import { apiClient, endpoints, unwrap } from '@core/api';
import { getActiveAccount } from '@core/store/accountSelectionStore';
import { toLocation } from './mappers/location.js';

function resolvedAccountApiKeyHeaders() {
  const apiKey = String(getActiveAccount()?.apiKey || '').trim();
  return apiKey ? { Authorization: `ApiKey ${apiKey}` } : undefined;
}

const LOCATIONS_PAGE_SIZE = 100;

function asLocationList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];

  const candidates = [
    data.locations,
    data.items,
    data.records,
    data.results,
    data.rows,
    data.list,
    data.data,
  ];

  return candidates.find(Array.isArray) ?? [];
}

function locationListQuery({ accountId, page }) {
  return {
    account_id: accountId,
    page,
    limit: LOCATIONS_PAGE_SIZE,
  };
}

/**
 * @param {{ accountId?: string }} [input]
 * @returns {Promise<import('@core/types').Location[]>}
 */
export async function listLocations({ accountId } = {}) {
  const headers = resolvedAccountApiKeyHeaders();
  const firstRaw = await apiClient.get(endpoints.reference.locations, {
    headers,
    query: locationListQuery({ accountId, page: 1 }),
  });
  const first = unwrap(firstRaw);
  const list = [...asLocationList(first.data)];
  const totalCount = first.meta?.totalCount;
  const totalPages =
    first.meta?.totalPages ??
    (totalCount ? Math.ceil(totalCount / (first.meta?.perPage || LOCATIONS_PAGE_SIZE)) : 1);

  for (
    let page = 2;
    page <= totalPages && (totalCount === undefined || list.length < totalCount);
    page += 1
  ) {
    const raw = await apiClient.get(endpoints.reference.locations, {
      headers,
      query: locationListQuery({ accountId, page }),
    });
    const { data } = unwrap(raw);
    list.push(...asLocationList(data));
  }

  return list.map(toLocation).filter((l) => l.isActive && l.isApproved);
}
/**
 * @param {string} id
 * @returns {Promise<import('@core/types').Location | null>}
 */
export async function getLocationById(id) {
  if (!id) return null;
  const raw = await apiClient.get(endpoints.reference.locationById(id));
  const { data } = unwrap(raw);
  return toLocation(data ?? raw);
}

/**
 * @param {string} id
 * @returns {Promise<object | null>}
 */
export async function getLocationOrgContext(id) {
  if (!id) return null;
  const raw = await apiClient.get(endpoints.reference.locationById(id), {
    headers: resolvedAccountApiKeyHeaders(),
    query: { include_org_settings: true },
  });
  const { data } = unwrap(raw);
  return data ?? raw;
}

/**
 * @param {{ locationId: string, brandId: string }} input
 * @returns {Promise<object | null>}
 */
export async function getLocationBrandOrgSettings({ locationId, brandId }) {
  if (!locationId || !brandId) return null;
  const raw = await apiClient.get(
    endpoints.reference.locationBrandOrgSettings(locationId, brandId),
    {
      headers: resolvedAccountApiKeyHeaders(),
    },
  );
  const { data } = unwrap(raw);
  return data ?? raw;
}

/**
 * @param {object} payload
 * @returns {Promise<any>}
 */
export async function checkDeliveryAvailability(payload) {
  const raw = await apiClient.post(endpoints.delivery.availabilityCheck, {
    data: payload,
  });
  return unwrap(raw);
}
